// src/demo/annualReviewDemoLogic.js

import React from 'react';
import { MODAL_TYPES } from './constants/DemoModalTypes';
import DemoAcknowledgmentModal from '../components/shared/DemoAcknowledgmentModal';
import MeetingDebriefingRoom from '../components/domains/advisor-portal/MeetingDebriefingRoom';
import ShareQuestionnaireModal from '../components/domains/advisor-portal/ShareQuestionnaireModal';
import { DEMO_TRANSCRIPT as SOURCE_TRANSCRIPT } from './annualReviewDemoTranscript';

// Re-export for convenience
export { MODAL_TYPES };

// ——— Modal Types and Templates ———
export const MODAL_TEMPLATES = {
  [MODAL_TYPES.SHARE_QUESTIONNAIRE]: {
    title: "✉️ Share Questionnaire",
    description: "You're about to send a secure link to the client to complete their questionnaire.",
    acknowledgementButtonLabel: "Send Link",
    component: ShareQuestionnaireModal,
    showDismissButton: true,
    showSendLinkButton: true,
    showCreateTaskButton: false,
    showViewResearchButton: false
  },

  [MODAL_TYPES.AI_SIDEBAR_INTRODUCTION]: {
    title: "🤖 Tool Introduction: AI Assistant",
    description: "The AI Assistant helps you navigate complex client conversations by providing " +
      "real-time insights, suggestions, and compliance checks based on your firm's " +
      "policies and the client's profile. It monitors the conversation for key topics " +
      "and automatically surfaces relevant information when needed.",
    isAcknowledgmentModal: true,
    component: DemoAcknowledgmentModal,
    buttonText: "Let's Begin",
    nextModalType: MODAL_TYPES.CALL_CONFIRMED
  },

  [MODAL_TYPES.CALL_CONFIRMED]: {
    title: "📞 Call Started",
    description: "Great! The call is now live and being recorded.",
    isAcknowledgmentModal: true,
    component: DemoAcknowledgmentModal,
    buttonText: "Begin Meeting",
    shouldBeginRecording: true,
    nextModalType: MODAL_TYPES.TRANSCRIPTION_INTRODUCTION
  },

  [MODAL_TYPES.TRANSCRIPTION_INTRODUCTION]: {
    title: "🛠️ Tool Introduction: Live Transcription",
    description: "Captures your advisor‐client conversation in real time, " +
      "displaying a scrolling, speaker-labeled transcript in the sidebar—like closed captions " +
      "for your meeting. It highlights relevant client statements based on CRM context " +
      "(e.g., income, goals) and passively cues next steps without interrupting your flow. " +
      "Transcripts are securely stored to support auditability, post-call documentation, " +
      "and compliance workflows.",
    secondaryNote: "Settings for this feature can be managed globally at the firm level or granted to " +
      "individual advisors for pause, stop, or disablement during calls. " +
      "Depending on your jurisdiction, you may need to disclose or obtain consent before " +
      "recording or transcribing conversations. The transcript is encrypted, " +
      "tamper-evident, and archived in accordance with applicable compliance rules.",
    acknowledgementButtonLabel: "Got It",
    spotlightTarget: "#modal-nav-btn-TRANSCRIPTION_INTRODUCTION",
    showConfirmOnly: true,
    showDismissButton: false,
  },

  [MODAL_TYPES.SMART_NAVIGATION]: {
    title: "🔗 Tool Introduction: Smart Navigation",
    phased: true,
    secondaryTitle: "🔗 Smart Navigation: Open Goals & Legacy",
    description: "Smart Navigation is your AI-powered co-pilot, designed to streamline your workflow " +
      "by anticipating which CRM pages you'll need next—before you even click. It learns " +
      "from your firm's activity patterns and individual usage behavior over time, " +
      "adapting its suggestions based on context, keyword detection, and advisor preferences.",
    secondaryNote: "Firms can configure Smart Navigation at the global and individual advisor level via " +
      "CRM settings. Suggestions are intentionally minimal and context-specific, " +
      "appearing only when relevant.",
    acknowledgementButtonLabel: "Got It",
    spotlightTarget: "#modal-nav-btn-SMART_NAVIGATION",
    dismissSpotlightTarget: "#modal-dismiss-btn-SMART_NAVIGATION",
    dismissSpotlightLabel: "Let's dismiss this suggestion for now",
    showDismissButton: true,
    dismissButtonLabel: "Dismiss",
    buttonLabel: "Open Goals & Legacy",
    showNavigationButtons: true,
    showViewResearchButton: false, 
    useInternalNavigation: true,
    targetPage: "/advisor-portal/client-database/household-profile/253/client-profile/48/goals-legacy",
    contextDetectionSummary:
      "The AI Sidebar Assistant detected Thomas' promotion and early-retirement discussion and recommends opening the Goals & Legacy page."
  },

  [MODAL_TYPES.OPEN_ALLOCATIONS]: {
    title: "📊 Smart Navigation: Household Asset Allocation",
    description: "The system recognized a discussion centered around the household's balance sheet " +
      "and asset base.",
    secondaryNote: "Based on learned patterns, this is typically when advisors review the Asset " +
      "Allocations page to assess current holdings or rebalancing needs.",
    acknowledgementButtonLabel: "Got It",
    showNavigationButtons: true,
    showDismissButton: true,
    buttonLabel: "Open Asset Allocations",
    useInternalNavigation: true,
    targetPage: "/portfolio-management/household-allocations/253",
    preserveSidebar: true,
    nextModalType: MODAL_TYPES.ALLOC_OVERVIEW,
    spotlightTarget: "#modal-nav-btn-OPEN_ALLOCATIONS",
    disableDismiss: true
  },

  [MODAL_TYPES.ALLOC_OVERVIEW]: {
    title: "🏦 Asset Allocation Overview",
    description: "Welcome to your Household Asset Allocation page. Here you can see how client assets are distributed across accounts, sectors, and strategies in addition to performance tracking at the individual asset, account, or household level.",
    secondaryDescription: "Take a moment to orient yourself—next we'll dive into performance trends.",
    showConfirmOnly: true,
    acknowledgementButtonLabel: "Got It",
    pauseLiveTranscriptionOnOpen: false,
    showDismissButton: false
  },

  [MODAL_TYPES.RETURN_REVIEW]: {
    title: "✅ Return to Annual Review",
    description: "You've reviewed performance and holdings. When you're ready, click below to go back to your Annual Review and continue the demo.",
    showConfirmOnly: true,
    acknowledgementButtonLabel: "Return to Annual Review",
    pauseLiveTranscriptionOnOpen: false,
    useInternalNavigation: true,
    targetPage: "/advisor-portal/client-database/household-profile/253/ar-profile/2025/1",
    useConfirmHandler: true,
    showDismissButton: false,
    dismissButtonLabel: undefined
  },

  [MODAL_TYPES.TASK_CREATION]: {
    title: "📋 Tool Introduction: Intelligent Task Recommendations",
    phased: true,
    secondaryTitle: "📋 Intelligent Task Recommendation: Updated Risk Assessment",
    description: "Effortlessly stay on top of follow-ups with Intelligent Task Recommendations. " +
      "Add reminders at any point, or let the AI Assistant surface actionable items " +
      "directly from the conversation—such as client requests or key next steps.",
    secondaryNote: "Every confirmed task is automatically linked to the client's record for seamless " +
      "tracking and accountability.",
    acknowledgementButtonLabel: "Got It",
    contextDetectionSummary: "Would you like to send the link for the risk assessment questionnaire now or create a task for later?",
    buttonLabel: "Create Task",
    showDismissButton: true,
    showSendLinkButton: true,
    showCreateTaskButton: true,
    showViewResearchButton: false,
    nextModalType: MODAL_TYPES.SHARE_QUESTIONNAIRE,
    onSendLink: true,
    spotlightTarget: "#modal-send-link-btn-TASK_CREATION",
    spotlightLabel: "Click here to send the questionnaire link now",
    disableDismiss: true
  },

  [MODAL_TYPES.SALES_REMINDER_STOCK_OPTIONS]: {
    title: "🧠 Tool Introduction: Guided Prompt",
    phased: true,
    secondaryTitle: "📚 Guided Prompt: Stock Options R&D Report",
    description: "Your Guided Prompt tool listens in on key topics—like equity compensation—and quietly surfaces private, context-aware suggestions in the AI Assistant panel. " +
      "It uses client data and firm best practices to recommend tailored questions or next steps without disrupting your conversation. " +
      "Prompts appear one at a time and can be dismissed if not relevant, so you stay focused on the client.",
    secondaryDescription: "Employees often misunderstand how RSUs and stock options work. This explainer provides a visual comparison of RSUs, ISOs, and NSOs, their vesting structures, and tax consequences. " +
      "Use this when guiding clients through compensation planning.",
    secondaryNote: "Your firm can configure prompt sensitivity and delivery under Settings → AI Assistant. All suggestions are timestamped, logged to the call record, and archived to meet compliance requirements.",
    acknowledgementButtonLabel: "Got It",
    contextDetectionSummary: "The system detected a discussion of equity compensation and RSUs—a topic the client highlighted in the pre-review questionnaire. Based on the conversation flow, " +
      "this is an ideal moment to confirm stock option details or reference any relevant R&D reports. Consider discussing vesting schedules, tax implications, or arranging a deeper follow-up review.",
    showDismissButton: true,
    showAddReminderButton: true,
    disableAddReminder: true,
    showViewResearchButton: true,
    modalType: MODAL_TYPES.SALES_REMINDER_STOCK_OPTIONS,
    researchModalType: MODAL_TYPES.SALES_REMINDER_STOCK_OPTIONS,
    analyticsTag: "ai_assistant_stock_options_prompt",
    spotlightTarget: "#modal-research-btn-SALES_REMINDER_STOCK_OPTIONS",
    disableDismiss: true,
    dismissSpotlightTarget: "#modal-add-reminder-btn-SALES_REMINDER_STOCK_OPTIONS",
    dismissSpotlightLabel: "Click here to add a reminder about stock options follow-up",
    researchSummary: "## 📌 Summary Question\n" +
      "What's the impact of RSUs on my tax plan and when do I owe taxes?\n\n" +
      "## 📄 Award Overview [view documents](/data/Thomas_Borer_RSU_Award_Letter_Styled.pdf)\n" +
      "**Awardee:** Thomas Borer\n" +
      "**Employer:** Pfizer Inc.\n" +
      "**Position:** Senior Director, Clinical Operations\n" +
      "**Grant Date:** January 15, 2025\n" +
      "**Total RSUs Granted:** 2,500\n" +
      "**Vesting Schedule:** 25% per year over 4 years beginning January 15, 2026\n" +
      "**Performance Bonus:** $37,500 (payable March 2025; taxed as ordinary income)\n\n" +
      "## 📊 Key Planning Considerations\n" +
      "- **Income Spike Management:** Each January vesting adds to your ordinary income (625 RSUs/year).\n" +
      "- **Capital Gains Optimization:** Holding shares >1 year post-vesting shifts gains to long-term capital gains (lower rate).\n" +
      "- **Withholding Strategy:** Employers may withhold ~22–37% of RSU value at vesting. If under-withheld, you may face unexpected tax due in April. Recommended: Review W-4 and/or make quarterly estimated payments.\n" +
      "- **Bonus Timing Sync:** Your $37,500 bonus in March 2025 and RSU vesting in Jan 2026 may push you into a higher bracket in 2025–2026.\n\n" +
      "## 💰 Taxation Timing and Treatment\n\n" +
      "| Event                | When                              | Tax Impact                             | Tax Type                |\n" +
      "|----------------------|-----------------------------------|----------------------------------------|-------------------------|\n" +
      "| Grant of RSUs        | Jan 15, 2025                      | No immediate tax                       | None                    |\n" +
      "| Vesting              | Jan 15 of each year (2026–2029)   | RSUs converted to shares; value taxed  | Ordinary Income (W-2)   |\n" +
      "| Holding after Vesting| Any period after each vest date    | Gains from appreciation taxed at sale  | Capital Gains           |\n" +
      "| Sale of Shares       | When shares sold                   | Difference from FMV at vesting is taxed| Capital Gains           |\n\n" +
      "_IRS Source: IRS Pub 525 - Taxable and Nontaxable Income_\n\n" +
      "> Example: If 625 RSUs vest on Jan 15, 2026, and FMV is $50/share → $31,250 is added to W-2 wages for 2026.\n\n" +
      "## ✅ Actionable Recommendations\n\n" +
      "| Item                | Action                                                         |\n" +
      "|---------------------|----------------------------------------------------------------|\n" +
      "| Tax Withholding     | Work with HR/payroll to confirm RSU tax withholding rate; consider adjusting W-4 |\n" +
      "| Financial Planning  | Meet with tax advisor to model income over 2025–2029; account for RSU and bonus timing |\n" +
      "| Sell or Hold Strategy | Decide per tranche if holding RSUs long-term aligns with your financial goals and risk tolerance |\n" +
      "| Capital Gains Strategy| Track acquisition/vesting dates; plan for long-term gains if possible |\n\n" +
      "_Sources: IRS Publication 525, Fidelity RSU Guide, Pfizer RSU Award Letter (2025)_"
  },

  [MODAL_TYPES.SALES_REMINDER_PLANNING_GOALS]: {
    title: "🧠 Guided Prompt: Goals & Legacy Discussion",
    description: "The AI Assistant identified an opportunity to confirm alignment between the client's current goals and planning horizon. " +
      "This is a strong moment to revisit target timelines, major upcoming milestones, or adjustments to key objectives based on recent life events or financial shifts.",
    showDismissButton: true,
    contextDetectionSummary: "Strong moment to revisit target timelines or adjustments based on recent life events.",
    showAddReminderButton: true,
    showViewResearchButton: false,
    analyticsTag: "ai_assistant_planning_goals_prompt",
    phased: true,
    initialPhase: 2,
    spotlightTarget: "#modal-add-reminder-btn-SALES_REMINDER_PLANNING_GOALS",
    spotlightLabel: "Click here to add a reminder about planning goals follow-up",
    dismissSpotlightTarget: "#modal-add-reminder-btn-SALES_REMINDER_PLANNING_GOALS",
    dismissSpotlightLabel: "Click here to add a reminder about planning goals follow-up",
    disableDismiss: true
  },

  [MODAL_TYPES.SALES_REMINDER_ALTERNATIVES]: {
    title: "📊 Tool Introduction: Sales Coach",
    phased: true,
    description: "Your Sales Coach AI Assistant provides discreet, real-time guidance during calls. " +
      "It listens for portfolio-diversification cues and pulls in client data to surface context-aware prompts " +
      "or suggested questions—without ever interrupting your conversation flow.",
    secondaryNote: "Settings for suggestion types, frequency, and Q&A access can be managed globally or per-advisor " +
      "under Settings → AI Assistant. All prompts and advisor interactions are timestamped, logged, " +
      "and archived for compliance.",
    acknowledgementButtonLabel: "Got It",
    secondaryTitle: "📊 Sales Coach: Alternative Investments",
    contextDetectionSummary: "The AI detected a discussion around portfolio diversification—now's the perfect time to introduce private equity or other alternative investments for better risk-adjusted returns.",
    spotlightTarget: "#modal-research-btn-SALES_REMINDER_ALTERNATIVES",
    spotlightLabel: "Click here to view detailed research about alternative investments",
    disableDismiss: true,
    researchSummary: "**Horizon Growth Partners Fund I**\n" +
      "*Alpha Ventures Group – Late-Stage Venture Capital*\n\n" +
      "**Fund Highlights:**\n" +
      "- **Target IRR:** 18–22%\n" +
      "- **Minimum Investment:** $200,000\n" +
      "- **Investment Horizon:** 8–10 years\n" +
      "- **Vintage Year:** 2024\n" +
      "- **Fund Type:** Growth; **Risk Level:** Medium-High\n\n" +
      "**Summary:**\n" +
      "Horizon Growth Partners Fund I is targeting established tech and healthcare unicorns " +
      "on the cusp of IPO or strategic acquisition. With an 8% preferred return and a 20% carry " +
      "above that hurdle, the fund aims for an 18–22% IRR. Capital calls occur over the first 24 months " +
      "(subscription period of 12 months), with distributions beginning in year 4. Liquidity is " +
      "subject to a 5-year lock-up.\n\n" +
      "**Key Terms:**\n" +
      "- **Management Fee:** 2.0% annually\n" +
      "- **Carried Interest:** 20% (above 8% preferred return)\n" +
      "- **Preferred Return:** 8%\n" +
      "- **Lock-Up Period:** 5 years\n" +
      "- **Subscription Period:** 12 months from initial closing\n" +
      "- **Liquidity:** Distributions start in Year 4",
    showDismissButton: true,
    showAddReminderButton: true,
    analyticsTag: "ai_assistant_alternatives_prompt",
    externalUrl: "http://localhost:3000/portfolio-management/alternative-assets/private-equity",
    dismissSpotlightTarget: "#modal-add-reminder-btn-SALES_REMINDER_ALTERNATIVES",
    dismissSpotlightLabel: "Click here to add a reminder about alternative investments follow-up"
  },

  [MODAL_TYPES.SMART_FOCUS]: {
    title: "🧠 Tool Introduction: Smart Focus",
    secondaryTitle: "🧠 Smart Focus: Portfolio Performance",
    description: "Smart Focus dims everything except the CRM section you're about to review, " +
      "drawing your attention exactly where you need it.",
    acknowledgementButtonLabel: "Got It",
    showDismissButton: true,
    showConfirmOnly: false,
    buttonLabel: "Focus",
    secondaryDescription: "Let's examine your portfolio's performance trends. This chart shows how your investments have grown over time.",
    phased: ({ toolIntroduction }) => Boolean(toolIntroduction),
    spotlightTarget: "#modal-nav-btn-SMART_FOCUS",
    spotlightLabel: "",
    dismissSpotlightTarget: "#modal-action-btn-SMART_FOCUS",
    dismissSpotlightLabel: ""
  },

  [MODAL_TYPES.ESTATE_PLANNING]: {
    title: "📊 Sales Coach: Estate Planning Services",
    phased: true,
    initialPhase: 2,
    description: "The AI detected an opportunity to discuss estate planning services - this is a critical area where many clients need guidance.",
    secondaryNote: "Over 67% of Americans lack a basic will. Effective estate planning preserves " +
      "up to 30% more wealth across generations by reducing probate costs and taxes.",
    showDismissButton: true,
    showAddReminderButton: true,
    showViewResearchButton: true,
    spotlightTarget: "#modal-research-btn-ESTATE_PLANNING",
    spotlightLabel: "Click here to view estate planning service details",
    disableDismiss: true,
    researchTitle: "R&D Report: Estate Planning Services",
    researchSummary: "## Estate Planning Services Overview\n\n" +
      "### 📊 Key Statistics\n" +
      "- 67% of Americans lack a basic will\n" +
      "- Proper estate planning can preserve up to 30% more wealth\n" +
      "- 43% of clients over 55 have outdated estate documents\n\n" +
      "### 🎯 Client Benefits\n" +
      "- Minimize probate costs and estate taxes\n" +
      "- Ensure assets transfer according to wishes\n" +
      "- Protect beneficiaries and reduce family conflicts\n" +
      "- Coordinate with retirement and tax planning\n\n" +
      "### 💼 Our Services\n" +
      "- Comprehensive estate document review\n" +
      "- Will and trust creation/updates\n" +
      "- Beneficiary designation audit\n" +
      "- Tax-efficient wealth transfer strategies\n" +
      "- Family meeting facilitation\n\n" +
      "### 📋 Next Steps\n" +
      "1. Review current estate documents\n" +
      "2. Update beneficiary designations\n" +
      "3. Schedule meeting with estate attorney\n" +
      "4. Create action plan for updates\n\n" +
      "### 💡 Discussion Points\n" +
      "- Recent life changes affecting estate plans\n" +
      "- Current wealth transfer goals\n" +
      "- Family dynamics and inheritance concerns\n" +
      "- Charitable giving objectives",
    dismissSpotlightTarget: "#modal-add-reminder-btn-ESTATE_PLANNING",
    dismissSpotlightLabel: "Click here to add a reminder about estate planning follow-up"
  },

  [MODAL_TYPES.POST_MEETING_TASKS]: {
    title: "Intelligent Task Recommendations: Meeting Wrap-Up",
    showViewResearchButton: false,
    showDismissButton: true,
    showCreateTaskButton: true,
    secondaryNote: "Based on today's wrap-up, these follow-up items have been identified:",
    taskList: [
      "Send Tax Analysis & Meeting Recap Email",
      "Send Stock Options R&D Report",
      "Send Risk Tolerance Analysis",
      "Follow Up on Alternative Investments",
      "Follow Up on Estate Plans",
      "Schedule Next Meeting"
    ],
    completedTasks: ["Send Risk Tolerance Analysis"],
    buttonLabel: "Create Tasks",
    defaultChecked: true,
    spotlightTarget: "#modal-create-task-btn-POST_MEETING_TASKS",
    spotlightLabel: "Click here to create these follow-up tasks",
    disableDismiss: true
  },

  [MODAL_TYPES.COMPLETE_DEMO]: {
    title: "Demo Complete",
    description: "You've finished the Annual Review demo!",
    acknowledgementButtonLabel: "Complete Demo",
    buttonLabel: "Complete Demo",
    detailsModalType: MODAL_TYPES.COMPLETE_DEMO,
    showConfirmOnly: true,
    showDismissButton: false,
    data: { 
      showDebriefingRoom: true,
      isFinalStep: true
    },
    // No follow-on modal; clean exit
    nextModalType: null
  },

  [MODAL_TYPES.SHOW_DEBRIEFING_ROOM]: {
    component: MeetingDebriefingRoom,
    title: "Meeting Debrief",
    isAcknowledgmentModal: false,
    showConfirmOnly: false,
    showDismissButton: false
  },

};

// ——— Timing constants for demo playback ———
export const DEMO_TIMING = {
  MIN_DELAY_MS: 50, //500
  MAX_DELAY_MS: 100, //1000
  DEFAULT_DELAY_MS: 150 //750
};

// ——— Helper: "MM:SS" → seconds ———
export function normalizeTimeString(timeStr) {
  const [min = 0, sec = 0] = (timeStr || "00:00").split(':').map(Number);
  return min * 60 + sec;
}

// ——— Helper: Get demo step for action type ———
export function getDemoStepForActionType(type) {
  switch (type) {
    case MODAL_TYPES.OPEN_ALLOCATIONS:
      return 5;
    case MODAL_TYPES.ALLOC_OVERVIEW:
      return 6;
    case MODAL_TYPES.ALLOC_HOLDING:
      return 8;
    case MODAL_TYPES.RETURN_REVIEW:
      return 9;
    default:
      return null;  // Let the caller handle default case
  }
}

// Consolidate action mappings into a single source of truth
export const ACTION_MAPPINGS = {
  'START_DEMO': MODAL_TYPES.AI_SIDEBAR_INTRODUCTION,
  'START_RECORDING': MODAL_TYPES.TRANSCRIPTION_INTRODUCTION,
  'CALL_CONFIRMED': MODAL_TYPES.CALL_CONFIRMED,
  'SMART_NAVIGATION': MODAL_TYPES.SMART_NAVIGATION,
  'OPEN_ALLOCATIONS': MODAL_TYPES.OPEN_ALLOCATIONS,
  'ALLOC_OVERVIEW': MODAL_TYPES.ALLOC_OVERVIEW,
  'ALLOC_HOLDING': MODAL_TYPES.ALLOC_HOLDING,
  'RETURN_REVIEW': MODAL_TYPES.RETURN_REVIEW,
  'END_DEMO': MODAL_TYPES.END_DEMO
};

// Map the transcript actions using the consolidated mappings
const mappedTranscript = SOURCE_TRANSCRIPT.map(entry => {
  if (!entry.action) return entry;

  return {
    ...entry,
    action: {
      ...entry.action,
      type: ACTION_MAPPINGS[entry.action.type] || entry.action.type
    }
  };
});

// Export the mapped transcript
export const DEMO_TRANSCRIPT = mappedTranscript;

// Re-export the notes content from the transcript file
export {
  DEMO_NOTES_SUMMARY,
  DEMO_NOTES_UPDATES,
  DEMO_NOTES_TASKS
} from './annualReviewDemoTranscript';
